import React, { useState, useEffect } from 'react';
import { config } from './config';

/**
 * Unsubscribe page - publicly accessible (no authentication required)
 * User lands here from email link with token in query parameter
 */
function UnsubscribePage() {
    const [token, setToken] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);

    useEffect(() => {
        // Extract token from URL query parameter
        const urlParams = new URLSearchParams(window.location.search);
        const tokenParam = urlParams.get('token');

        if (tokenParam) {
            setToken(tokenParam);
        } else {
            setError('Chýba overovací token. Prosím použite odkaz z emailu.');
        }
    }, []);

    const handleUnsubscribe = async () => {
        if (!token) {
            setError('Chýba overovací token.');
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            const response = await fetch(`${config.apiUrl}/unsubscribeFromEmails`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ token })
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
                setEmail(data.email);
            } else {
                setError(data.message || 'Nepodarilo sa odhlásiť z emailov.');
            }
        } catch (err) {
            console.error('Unsubscribe error:', err);
            setError('Nepodarilo sa spojiť so serverom. Skúste to prosím neskôr.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            <div style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '24px',
                padding: '48px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                maxWidth: '500px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
                {success ? (
                    <>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
                        <h1 style={{
                            fontSize: '28px',
                            fontWeight: '700',
                            color: '#2d3748',
                            marginBottom: '12px',
                            letterSpacing: '-0.5px'
                        }}>Úspešne odhlásené</h1>
                        <p style={{
                            fontSize: '16px',
                            color: '#64748b',
                            marginBottom: '24px',
                            lineHeight: '1.6'
                        }}>
                            Email <strong>{email}</strong> bol odhlásený z marketingových emailov SmartBloček.
                        </p>
                        <p style={{
                            fontSize: '14px',
                            color: '#94a3af',
                            lineHeight: '1.6'
                        }}>
                            Ak si to rozmyslíte, môžete sa prihlásiť späť v nastaveniach svojho účtu.
                        </p>
                        <a
                            href="https://smartblocek.sk"
                            style={{
                                display: 'inline-block',
                                marginTop: '24px',
                                padding: '12px 24px',
                                background: '#667eea',
                                color: '#fff',
                                borderRadius: '12px',
                                textDecoration: 'none',
                                fontWeight: '600',
                                fontSize: '16px'
                            }}
                        >
                            Návrat na SmartBloček
                        </a>
                    </>
                ) : error ? (
                    <>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>❌</div>
                        <h1 style={{
                            fontSize: '28px',
                            fontWeight: '700',
                            color: '#2d3748',
                            marginBottom: '12px',
                            letterSpacing: '-0.5px'
                        }}>Chyba</h1>
                        <p style={{
                            fontSize: '16px',
                            color: '#64748b',
                            marginBottom: '24px',
                            lineHeight: '1.6'
                        }}>
                            {error}
                        </p>
                        <p style={{
                            fontSize: '14px',
                            color: '#94a3af',
                            lineHeight: '1.6'
                        }}>
                            Ak problém pretrváva, kontaktujte nás prosím.
                        </p>
                    </>
                ) : (
                    <>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>📧</div>
                        <h1 style={{
                            fontSize: '28px',
                            fontWeight: '700',
                            color: '#2d3748',
                            marginBottom: '12px',
                            letterSpacing: '-0.5px'
                        }}>Odhlásenie z emailov</h1>
                        <p style={{
                            fontSize: '16px',
                            color: '#64748b',
                            marginBottom: '32px',
                            lineHeight: '1.6'
                        }}>
                            Chcete sa odhlásiť z marketingových emailov SmartBloček?
                        </p>
                        <button
                            onClick={handleUnsubscribe}
                            disabled={isProcessing || !token}
                            style={{
                                padding: '14px 32px',
                                background: isProcessing ? '#cbd5e1' : '#dc2626',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: isProcessing || !token ? 'not-allowed' : 'pointer',
                                transition: 'background 0.2s',
                                marginBottom: '16px'
                            }}
                        >
                            {isProcessing ? '⏳ Spracúvam...' : 'Odhlásiť z emailov'}
                        </button>
                        <div>
                            <a
                                href="https://smartblocek.sk"
                                style={{
                                    fontSize: '14px',
                                    color: '#667eea',
                                    textDecoration: 'underline'
                                }}
                            >
                                Zrušiť a vrátiť sa späť
                            </a>
                        </div>
                    </>
                )}
            </div>

            <div style={{
                marginTop: '24px',
                fontSize: '12px',
                color: '#e5e7eb',
                textAlign: 'center'
            }}>
                © SmartBloček • smartblocek.sk
            </div>
        </div>
    );
}

export default UnsubscribePage;
